import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react';
import { testimonials, business } from '../mock';
import BotanicalDecor from './BotanicalDecor';
import Marquee from './Marquee';

const avatarTone = ['var(--sage)', 'var(--pill-yellow)', 'var(--pill-lilac)', 'var(--pill-green)'];

function Card({ t, idx, animate }) {
  return (
    <div
      className={`${animate ? 'testi-card ' : ''}bg-white rounded-3xl p-8 flex flex-col h-full w-full`}
      style={{ border: '1px solid var(--line)', boxShadow: '0 18px 40px -30px rgba(30,59,44,0.5)', animationDelay: animate ? `${idx * 0.08}s` : undefined }}
    >
      <Quote size={34} style={{ color: 'var(--sage)' }} fill="currentColor" />
      <p className="mt-4 flex-1 text-[15px]" style={{ color: 'var(--ink)', lineHeight: 1.7 }}>
        &ldquo;{t.quote}&rdquo;
      </p>
      <div className="flex items-center gap-3 mt-6 pt-6" style={{ borderTop: '1px solid var(--line)' }}>
        <span
          className="flex items-center justify-center rounded-full font-serif-display text-lg shrink-0"
          style={{ width: 48, height: 48, backgroundColor: avatarTone[idx % avatarTone.length], color: 'var(--forest)' }}
        >
          {t.name.charAt(0)}
        </span>
        <div>
          <div className="font-semibold" style={{ color: 'var(--forest)' }}>{t.name}</div>
          <div className="text-sm" style={{ color: 'var(--muted)' }}>{t.relation}</div>
        </div>
      </div>
    </div>
  );
}

export default function Testimonials() {
  const [start, setStart] = useState(0);
  const [paused, setPaused] = useState(false);
  const perView = 3;
  const total = testimonials.length;

  const prev = () => setStart((s) => (s - 1 + total) % total);
  const next = () => setStart((s) => (s + 1) % total);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setStart((s) => (s + 1) % total), 4000);
    return () => clearInterval(id);
  }, [paused, total]);

  const visible = Array.from({ length: perView }, (_, i) => testimonials[(start + i) % total]);

  return (
    <section id="reviews" className="section relative overflow-hidden" style={{ backgroundColor: 'var(--cream-2)' }}>
      <BotanicalDecor style={{ top: 40, left: -30, width: 180, height: 420 }} />
      <BotanicalDecor style={{ bottom: 20, right: -30, width: 180, height: 420 }} flip />

      <div className="container-x relative" style={{ zIndex: 1 }}>
        <div className="text-center max-w-[720px] mx-auto reveal">
          <p className="eyebrow mb-2">Testimonial</p>
          <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3.1rem)' }}>
            Hear from the wonderful families we care for
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mt-5">
            <div className="flex">
              {[0, 1, 2, 3, 4].map((s) => <Star key={s} size={18} fill="#E7B850" color="#E7B850" />)}
            </div>
            <span className="font-semibold" style={{ color: 'var(--forest)' }}>{business.rating}</span>
            <span style={{ color: 'var(--muted)' }}>&middot; {business.reviewCount} reviews on Google &amp; Facebook</span>
          </div>
        </div>

        {/* Desktop / tablet: 3-card carousel with controls */}
        <div className="hidden md:block mt-12 reveal" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <div className="grid md:grid-cols-3 gap-6">
            {visible.map((t, idx) => (
              <Card key={`${start}-${idx}`} t={t} idx={idx} animate />
            ))}
          </div>

          <div className="flex items-center justify-center gap-3 mt-10">
            <button onClick={prev} aria-label="Previous reviews" className="flex items-center justify-center rounded-full transition-colors duration-200 hover:bg-white" style={{ width: 48, height: 48, border: '1.5px solid var(--forest)', color: 'var(--forest)' }}>
              <ChevronLeft size={22} />
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStart(i)}
                  aria-label={`Reviews starting at ${i + 1}`}
                  style={{
                    width: i === start ? 26 : 9, height: 9, borderRadius: 999,
                    backgroundColor: i === start ? 'var(--forest)' : 'var(--line)',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </div>
            <button onClick={next} aria-label="Next reviews" className="flex items-center justify-center rounded-full transition-colors duration-200 hover:bg-white" style={{ width: 48, height: 48, border: '1.5px solid var(--forest)', color: 'var(--forest)' }}>
              <ChevronRight size={22} />
            </button>
          </div>
        </div>

        {/* Mobile: one moving row (marquee), pauses on hover/touch, equal-height cards */}
        <div className="md:hidden mt-10">
          <Marquee
            items={testimonials}
            itemWidth={300}
            gap={16}
            durationSec={42}
            renderItem={(t, i) => <Card t={t} idx={i} />}
          />
        </div>
      </div>
    </section>
  );
}
